import React, { memo, useEffect, useState } from 'react';

import isEqual from 'lodash/isEqual';
import { useFormContext } from 'react-hook-form';
import { DateTime } from 'luxon';

import {
	AlertComponent,
	AlertEmphasis,
	AlertStatus
} from '@vaa-component-lib/component.molecule.alert';
import { IconCarInsuranceComponent } from '@vaa-component-lib/component.atom.icons';

import { getSalesBanners } from '../../lib/utils/api.service';
import {
	SalesBannerData,
	SalesBannerBookingType,
	SalesBannerLocation,
	SalesBannerFlightDirection,
	SalesBannerRequest
} from '../../types/api';

import { MessageContainer } from './seasonal-message.styles';

const bookingTypeMap: { [key: string]: SalesBannerBookingType } = {
	FLIGHT_ONLY: 'flight-only',
	FLIGHT_HOTEL: 'flight-hotel',
	FLIGHT_CAR: 'flight-car',
	REWARD_FLIGHT: 'reward-flights',
	HOLIDAY: 'holidays',
	FLYDRIVE: 'flight-car'
};

const messageThemeMap: { [key: string]: AlertStatus } = {
	'alert-warning': AlertStatus.Warning,
	'alert-success': AlertStatus.Success,
	'alert-info': AlertStatus.Information,
	'alert-promotional': AlertStatus.Promotional
};

interface SeasonalMessageProps {
	data?: {
		searchType?: string;
		submissionType?: string;
		origin?: {
			airportCode?: string;
		};
		destination?: {
			airportCode?: string;
		};
		departing?: Date[];
		gateway?: string;
		passengers?: {
			adults?: number;
			teens?: number;
			children?: number;
			infants?: number;
		};
		location?: {
			locationUrl?: string;
		};
	};
}

const SeasonalMessageComponent = ({ data }: SeasonalMessageProps) => {
	const [request, setRequest] = useState<SalesBannerRequest | null>(null);
	const [messageData, setMessageData] = useState<SalesBannerData | null>(null);

	const { getValues } = useFormContext();
	const dataValues = getValues() || data || null;

	if (!dataValues) {
		return null;
	}

	const generateRequest = (): SalesBannerRequest | null => {
		const {
			gateway,
			origin,
			destination,
			searchType,
			submissionType,
			departing = [],
			passengers: {
				adults: numAdults = null,
				teens: numTeens = null,
				children: numChildren = null,
				infants: numInfants = null
			} = {},
			location
		} = dataValues;

		const originAirport = origin?.airportCode || gateway || null;
		const destinationAirport = destination?.airportCode || null;

		const normalisedDates = departing.map((date: string | DateTime) => {
			if (typeof date === 'string') {
				return new Date(date);
			} else if (DateTime.isDateTime(date)) {
				return date.toJSDate();
			} else {
				return null;
			}
		});

		const request = {
			bookingType: bookingTypeMap[searchType || submissionType] || null,
			originAirport,
			...(destinationAirport && { destinationAirport: destinationAirport }),
			departureDate: normalisedDates[0] || null,
			returnDate: normalisedDates[1] || null,
			flightDirection: (normalisedDates.length > 1
				? 'return'
				: 'one-way') as SalesBannerFlightDirection,
			numAdults,
			numTeens,
			numChildren,
			numInfants,
			...(location && { destinationURL: location?.locationUrl })
		};

		return originAirport || destinationAirport || location ? request : null;
	};

	const fetchSalesBanner = async (theRequest: SalesBannerRequest) => {
		try {
			const response = await getSalesBanners(
				'seasonal-messaging' as SalesBannerLocation,
				theRequest
			);

			setMessageData(
				(prevData) => response?.data?.data?.salesBanners[0] || null
			);
		} catch (error) {
			console.error('Error fetching sales banners:', error);
		}
	};

	const newRequest = generateRequest();

	useEffect(() => {
		if (newRequest && !isEqual(newRequest, request)) {
			fetchSalesBanner(newRequest);
			setRequest(newRequest);
		} else if (!newRequest) {
			setMessageData(null);
		}
	}, [newRequest, request]);

	return (
		<>
			<MessageContainer
				opened={!!messageData}
				data-cy="seasonal-message-component">
				{messageData && (
					<AlertComponent
						status={
							messageThemeMap[messageData.theme] || AlertStatus.Information
						}
						emphasis={AlertEmphasis.Low}
						iconChild={<IconCarInsuranceComponent />}
						title={messageData.title}>
						<div
							dangerouslySetInnerHTML={{
								__html: messageData.bodyHtml.replace('&nbsp;', ' ')
							}}
						/>
					</AlertComponent>
				)}
			</MessageContainer>
		</>
	);
};

export default memo(SeasonalMessageComponent);
